import { Box, BoxProps, TableRow } from '@mui/material';
import { styled } from '@mui/material/styles';

interface FlexProps extends BoxProps {
  direction?: 'row' | 'column' | 'row-reverse' | 'column-reverse';
  justify?:
    | 'flex-start'
    | 'flex-end'
    | 'center'
    | 'space-between'
    | 'space-around'
    | 'space-evenly';
  align?: 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline';
  wrap?: 'nowrap' | 'wrap' | 'wrap-reverse';
  gap?: number | string;
  fullWidth?: boolean;
  fullHeight?: boolean;
  padding?: number | string;
  paddingX?: number | string;
  paddingY?: number | string;
  paddingTop?: number | string;
  paddingBottom?: number | string;
  paddingLeft?: number | string;
  paddingRight?: number | string;
}

const shouldForwardProp = (prop: PropertyKey) =>
  ![
    'direction',
    'justify',
    'align',
    'wrap',
    'gap',
    'fullWidth',
    'fullHeight',
    'padding',
    'paddingX',
    'paddingY',
    'paddingTop',
    'paddingBottom',
    'paddingLeft',
    'paddingRight',
  ].includes(prop as string);

export const Flex = styled(Box, { shouldForwardProp })<FlexProps>(
  ({
    direction = 'row',
    justify = 'flex-start',
    align = 'flex-start',
    wrap = 'nowrap',
    gap = 0,
    fullWidth = false,
    fullHeight = false,
    padding,
    paddingX,
    paddingY,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    theme,
  }) => ({
    display: 'flex',
    flexDirection: direction,
    justifyContent: justify,
    alignItems: align,
    flexWrap: wrap,
    gap: typeof gap === 'number' ? theme.spacing(gap) : gap,
    width: fullWidth ? '100%' : 'auto',
    height: fullHeight ? '100%' : 'auto',

    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },

    padding:
      padding !== undefined
        ? typeof padding === 'number'
          ? theme.spacing(padding)
          : padding
        : undefined,
    paddingTop:
      paddingTop !== undefined
        ? typeof paddingTop === 'number'
          ? theme.spacing(paddingTop)
          : paddingTop
        : paddingY !== undefined
        ? typeof paddingY === 'number'
          ? theme.spacing(paddingY)
          : paddingY
        : undefined,
    paddingBottom:
      paddingBottom !== undefined
        ? typeof paddingBottom === 'number'
          ? theme.spacing(paddingBottom)
          : paddingBottom
        : paddingY !== undefined
        ? typeof paddingY === 'number'
          ? theme.spacing(paddingY)
          : paddingY
        : undefined,
    paddingLeft:
      paddingLeft !== undefined
        ? typeof paddingLeft === 'number'
          ? theme.spacing(paddingLeft)
          : paddingLeft
        : paddingX !== undefined
        ? typeof paddingX === 'number'
          ? theme.spacing(paddingX)
          : paddingX
        : undefined,
    paddingRight:
      paddingRight !== undefined
        ? typeof paddingRight === 'number'
          ? theme.spacing(paddingRight)
          : paddingRight
        : paddingX !== undefined
        ? typeof paddingX === 'number'
          ? theme.spacing(paddingX)
          : paddingX
        : undefined,
  })
);

interface MaxContentProps {
  position?: 'relative' | 'absolute' | 'fixed' | 'sticky';
}

export const MaxContent = styled('div')<MaxContentProps>(({ theme, position }) => ({
  width: '100%',
  maxWidth: '82.94%',
  margin: '0 auto',
  height: 'auto',
  position,
  overflowX: 'hidden',

  [theme.breakpoints.down('lg')]: {
    maxWidth: '90%',
  },

  [theme.breakpoints.down('md')]: {
    maxWidth: '95%',
  },

  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
  },
}));

export const TwoInputs = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),

  [theme.breakpoints.down('sm')]: {
    flexWrap: 'wrap',
    gap: 0,
  },
}));

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));
